import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, SearchIcon } from 'lucide-react';
import { AdminPasswordGate } from '../components/AdminPasswordGate';
import { useMetaTags } from '../hooks/useMetaTags';
import { exploreBridges, ExploreBridge } from '../data/exploreBridges';
import { programBridgeIndex } from '../data/programBridges';
import { degreeLevelOptions } from '../data/filterOptions';

// Internal directory of every /explore/{...} ad-copy page: the hand-authored
// advertiser pages, plus the generated degree + program combinations.
//
// There are several hundred generated pages, so the list is search-first —
// results are filtered by query and degree level and capped, rather than
// rendering everything at once.
const RESULT_LIMIT = 60;
function describeDestination(bridge: ExploreBridge): string {
  if (bridge.clicksSearch) return 'Media-Matchers Clicks Search';
  if (!bridge.destinationUrl) return 'Not set';
  return bridge.destinationUrl;
}
export function ExploreDirectoryPage() {
  useMetaTags({
    title: 'Explore Templates | FindOnlineDegrees',
    description: 'Directory of every Explore bridge page template and where each one sends traffic.',
    // An internal index of ad-copy variants shouldn't compete in search.
    robots: 'noindex, nofollow'
  });
  const [query, setQuery] = useState('');
  const [degreeFilter, setDegreeFilter] = useState<number | 'all'>('all');
  const advertiserBridges = Object.values(exploreBridges);
  const advertiserBrands = Array.from(new Set(advertiserBridges.map(bridge => bridge.brand)));
  const matches = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return programBridgeIndex.filter(entry => {
      if (degreeFilter !== 'all' && entry.degreeInterest !== degreeFilter) {
        return false;
      }
      if (!terms.length) return true;
      const haystack = `${entry.slug} ${entry.degreeLabel} ${entry.programLabel} ${entry.areaLabel} ${entry.mProgram}`.toLowerCase();
      return terms.every(term => haystack.includes(term));
    });
  }, [query, degreeFilter]);
  const visible = matches.slice(0, RESULT_LIMIT);

  // The shared PageLayout in App.tsx supplies the site nav and footer for
  // this route, so this page renders only its own content.
  return <AdminPasswordGate description="The Explore template directory is for the team. Enter the password to continue." data-id="element-1825" data-mp="7nbkoimmnubs">
      <div className="bg-white" data-id="element-1826" data-mp="cjafanyju8kn">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-id="element-1827" data-mp="gm6oxp642ucg">
          <header className="max-w-2xl" data-id="element-1828" data-mp="ldf4z41y5dyl">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-1829" data-mp="leq5mv03i5lh">
              Explore templates
            </h1>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed" data-id="element-1830" data-mp="qeci3283nht8">
              Advertiser ad copy lives at{' '}
              <code className="text-base text-gray-900" data-id="element-1831" data-mp="10afv534hp0uy">
                /explore/&#123;brand&#125;-&#123;version&#125;
              </code>
              . Every degree level and program combination has its own page at{' '}
              <code className="text-base text-gray-900" data-id="element-1832" data-mp="2ftdpi5fjz9rf">
                /explore/&#123;degree&#125;-&#123;program&#125;
              </code>
              , which passes that pairing straight into the match.
            </p>
          </header>

          {/* Advertiser ad copy — a short, hand-authored set */}
          {advertiserBrands.map(brand => <section key={brand} className="mt-14" data-id="element-1833" data-mp="33l2epcx2vi45">
              <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500" data-id="element-1834" data-mp="2yl69xbgk3mnj">
                {brand}
              </h2>
              <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1835" data-mp="2trlglwpwrguo">
                {advertiserBridges.filter(bridge => bridge.brand === brand).map(bridge => <li key={bridge.slug} className="flex" data-id="element-1836" data-mp="3mbk5hbyco3mv">
                      <Link to={`/explore/${bridge.slug}`} className="group flex w-full flex-col rounded-2xl border border-gray-200 p-6 transition-colors hover:border-[#e8594f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e8594f] focus-visible:ring-offset-2" data-id="element-1837" data-mp="3hi7hn41hw0v6">
                        <span className="text-xs font-semibold text-gray-400" data-id="element-1838" data-mp="2x1wmdtb8umrx">
                          /explore/{bridge.slug}
                        </span>
                        <h3 className="mt-2 text-lg font-bold text-gray-900" data-id="element-1839" data-mp="3krz8lssh8yim">
                          {bridge.cardHeadline}
                        </h3>
                        <p className="mt-2 text-sm text-gray-600 leading-relaxed" data-id="element-1840" data-mp="3b27baqsztrag">
                          {bridge.sectionHeading}
                        </p>
                        <dl className="mt-4 space-y-1 text-xs text-gray-500" data-id="element-1841" data-mp="3fynxvgkjnxlf">
                          <div className="flex gap-1.5" data-id="element-1842" data-mp="7je36tizhuno">
                            <dt className="font-semibold text-gray-700" data-id="element-1843" data-mp="cc76re2puwy6">
                              Layout:
                            </dt>
                            <dd data-id="element-1844" data-mp="1x0hvy3gnark9">
                              {bridge.layout === 'branded' ? 'Advertiser branded' : 'FindOnlineDegrees'}
                            </dd>
                          </div>
                          <div className="flex gap-1.5 min-w-0" data-id="element-1845" data-mp="21sj87pdtciaa">
                            <dt className="font-semibold text-gray-700 flex-shrink-0" data-id="element-1846" data-mp="1e371cw5ykmb5">
                              Goes to:
                            </dt>
                            <dd className="truncate" data-id="element-1847" data-mp="195oo4tkwrkxc">
                              {describeDestination(bridge)}
                            </dd>
                          </div>
                        </dl>
                        <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#e8594f]" data-id="element-1848" data-mp="1j0qh60ib1757">
                          View page
                          <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1849" data-mp="2ui8a04yckmcy" />
                        </span>
                      </Link>
                    </li>)}
              </ul>
            </section>)}

          {/* Generated degree + program pages — search first */}
          <section className="mt-16 border-t border-gray-200 pt-12" data-id="element-1850" data-mp="2547v0mrf8u5h">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-1851" data-mp="18hcszwc7l3gq">
              Degree &amp; program pages
            </h2>
            <p className="mt-2 text-gray-600" data-id="element-1852" data-mp="1dd1tc1w1yoe3">
              {programBridgeIndex.length.toLocaleString()} combinations. Search
              by program, area of study, degree level, or mProgram id.
            </p>

            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center" data-id="element-1853" data-mp="1m9av3n7i5z3e">
              <div className="relative flex-1" data-id="element-1854" data-mp="pnh9lgs2rn3r">
                <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" data-id="element-1855" data-mp="tqdl6nj76iy7" />
                <label htmlFor="template-search" className="sr-only" data-id="element-1856" data-mp="h81drtt4peao">
                  Search templates
                </label>
                <input id="template-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="e.g. accounting, nursing, 500" className="w-full rounded-lg border border-gray-300 py-3 pl-12 pr-4 text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#e8594f]" data-id="element-1857" data-mp="d5n15rg1zaru" />
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by degree level" data-id="element-1858" data-mp="3lo2lpvy2gjmd">
                {[{
                value: 'all' as const,
                label: 'All levels'
              }, ...degreeLevelOptions].map(option => {
                const active = degreeFilter === option.value;
                return <button key={option.label} type="button" aria-pressed={active} onClick={() => setDegreeFilter(option.value as number | 'all')} className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e8594f] focus-visible:ring-offset-2 ${active ? 'border-[#e8594f] bg-[#e8594f] text-white' : 'border-gray-300 text-gray-700 hover:border-gray-400'}`} data-id="element-1859" data-mp="18dxahbmm3w48">
                        {option.label}
                      </button>;
              })}
              </div>
            </div>

            <p className="mt-5 text-sm text-gray-500" role="status" data-id="element-1860" data-mp="3ja0bz9vihbwp">
              {matches.length === 0 ? 'No templates match that search.' : matches.length > visible.length ? `Showing ${visible.length} of ${matches.length.toLocaleString()} matches — keep typing to narrow it down.` : `${matches.length.toLocaleString()} ${matches.length === 1 ? 'match' : 'matches'}.`}
            </p>

            {visible.length > 0 && <ul className="mt-6 divide-y divide-gray-200 border-y border-gray-200" data-id="element-1861" data-mp="12kq3fbiwvkt">
                {visible.map(entry => <li key={entry.slug} data-id="element-1862" data-mp="frqnqyupqdql">
                    <Link to={`/explore/${entry.slug}`} className="group flex flex-col gap-1 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e8594f]" data-id="element-1863" data-mp="asvrov7qisoj">
                      <span className="min-w-0" data-id="element-1864" data-mp="1utqtd1tla6fd">
                        <span className="block font-semibold text-gray-900" data-id="element-1865" data-mp="1uvld4ct4jshv">
                          {entry.degreeLabel} in {entry.programLabel}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-gray-500" data-id="element-1866" data-mp="1l58x8xw98ni2">
                          /explore/{entry.slug} · {entry.areaLabel} ·
                          degreeInterest={entry.degreeInterest} · mProgram=
                          {entry.mProgram}
                        </span>
                      </span>
                      <span className="inline-flex flex-shrink-0 items-center gap-1.5 text-sm font-semibold text-[#e8594f]" data-id="element-1867" data-mp="28ul61wno6tju">
                        View
                        <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1868" data-mp="1ztv8ooirgv40" />
                      </span>
                    </Link>
                  </li>)}
              </ul>}
          </section>
        </div>
      </div>
    </AdminPasswordGate>;
}